import { isAfter, isBefore, parseISO } from 'date-fns';

export function calculateEnrollment(
  enrollment: Enrollment,
  invoices?: Invoice[],
) {
  const vatFree =
    enrollment?.discounts?.[0]?.discount?.code === 'VATFREE' ||
    enrollment?.discounts?.[0]?.discount?.code === '2025';

  const CPD = enrollment?.discounts?.[0]?.discount?.code === 'CPD999';
  const onsiteCpdPackage = enrollment?.client_data?.onsite_cpd_package === true;
  const courseLines = Array.isArray(enrollment?.metadata)
    ? enrollment.metadata
    : [];
  const subTotal =
    (CPD || onsiteCpdPackage) && courseLines.length === 4
      ? 99900
      : courseLines.reduce((a, b) => a + b.seat_price, 0);

  // Calculate discount amount based on discount conditions
  let discountAmount = 0;
  if (enrollment && enrollment.discounts.length > 0) {
    const discount = enrollment.discounts[0].discount;

    if (
      (discount.code === 'CPD999' || onsiteCpdPackage) &&
      enrollment.metadata.length === 4
    ) {
      // CPD999 special case - handled above in subTotal
      discountAmount = 0;
    } else if (discount.code !== 'VATFREE' && discount.code !== '2025') {
      const { config } = discount;
      if (config && config.condition) {
        const courses = enrollment.metadata.map((meta) => ({
          id: meta.detail_id,
          detail_id: meta.detail_id,
          standard_price: meta.seat_price,
          seat_price: meta.seat_price,
        }));

        if (config.condition === 'buyXGetY') {
          discountAmount = calculateBuyXGetYDiscount(courses, discount);
        } else if (config.condition === 'bulkDiscount') {
          discountAmount = calculateBulkDiscount(courses, discount);
        } else if (config.condition === 'validUntil') {
          discountAmount = calculateValidUntilDiscount(courses, discount);
        }
      } else {
        // Fallback to simple discount calculation
        discountAmount =
          discount.type === 'percent'
            ? (discount.value / 100) * subTotal
            : discount.value;
      }
    }
  }

  const paymentsAmount =
    enrollment && enrollment.payments && enrollment.payments.length > 0
      ? enrollment.payments
          .filter((x) => x.payment && x.payment.status === 'complete')
          .filter((x) => !x.payment.invoice_id)
          .reduce((a, b) => a + b.payment.amount, 0)
      : 0;

  const invoicesAmount =
    invoices && invoices.length > 0
      ? invoices
          .filter((x) => x.status === 'paid' || x.status === 'complete')
          .reduce((a, b) => a + b.due, 0)
      : 0;

  const paidAmount = invoicesAmount + paymentsAmount;

  const creditAmount =
    enrollment && enrollment.credits.length > 0
      ? enrollment.credits[0].credit.amount
      : 0;

  const realTotal = subTotal - discountAmount - creditAmount;

  const extrasTotal =
    enrollment && enrollment.metadata.length > 0
      ? subTotal - enrollment.metadata.reduce((a, b) => a + b.seat_price, 0)
      : 0;

  const after =
    enrollment &&
    isAfter(new Date(enrollment.created_at), new Date('2024-05-16 16:00:00'));

  const vat =
    enrollment?.discounts?.[0]?.discount?.code === 'VATFREE' ||
    enrollment?.discounts?.[0]?.discount?.code === '2025'
      ? 0
      : 0.2;

  return {
    paidAmount,
    subTotal,
    realTotal: after ? realTotal * (1 + vat) : 0,
    creditAmount,
    extrasTotal,
    discountAmount,
  };
}

function calculateBuyXGetYDiscount(courses: any[], discount: any): number {
  const { rules } = discount.config;

  // Abort if basket does not meet the minimum quantity needed to apply the discount
  if (rules.minimumQuantity > courses.length) {
    return 0;
  }

  const validAppliesFrom = courses.filter((course) =>
    rules.applyFrom.includes(course.detail_id),
  );
  const validAppliesTo = courses.filter((course) =>
    rules.applyTo.includes(course.detail_id),
  );

  // If validAppliesFrom is greater or equal than buyAmount and validApplyTo is greater or equal than getAmount then discount is valid
  const basketHasRequiredCourses = !!(
    validAppliesFrom.length >= rules.buyEventAmount &&
    validAppliesTo.length >= rules.getEventAmount
  );

  // Abort if basket does not meet the required courses needed to apply the discount
  if (!basketHasRequiredCourses) {
    return 0;
  }

  // Sort courses by price lowest to highest
  // Then filter out courses that do not match courses the discount can be applied to
  const sorted = [...courses]
    .sort((a, b) => {
      const ap = a.standard_price
        ? a.standard_price
        : a.seat_price
          ? a.seat_price
          : 0;
      const bp = b.standard_price
        ? b.standard_price
        : b.seat_price
          ? b.seat_price
          : 0;
      return ap - bp;
    })
    .filter((x) => {
      return rules.applyTo.includes(x.detail_id);
    });

  // Create an array of the quantity of courses that can be discounted
  const res = Array(rules.getEventAmount)
    .fill(null)
    // Map over the array and return the course at the index of the current iteration
    // This will return the first x courses that can be discounted
    .map((_, i) => {
      const item = sorted[i];
      if (!item) return null;
      const price = item.standard_price
        ? item.standard_price
        : item.seat_price
          ? item.seat_price
          : 0;
      const discountAmount =
        discount.type === 'percent'
          ? (discount.value / 100) * price
          : discount.value;

      return {
        ...item,
        discountAmount,
      };
    })
    .filter(Boolean);

  return res.reduce((a, b) => a + b.discountAmount, 0);
}

function calculateBulkDiscount(courses: any[], discount: any): number {
  const { rules } = discount.config;

  // Abort if basket does not meet the minimum quantity needed to apply the discount
  if (rules.minimumQuantity > courses.length) {
    return 0;
  }

  const items = courses
    .filter((x) => {
      return discount.events.find((y) => y.event_detail_id === x.detail_id);
    })
    .map((x) => {
      const price = x.standard_price
        ? x.standard_price
        : x.seat_price
          ? x.seat_price
          : 0;
      const discountAmount =
        discount.type === 'percent'
          ? (discount.value / 100) * price
          : discount.value;

      return {
        ...x,
        discountAmount,
      };
    });

  return items.reduce((a, b) => a + b.discountAmount, 0);
}

function calculateValidUntilDiscount(courses: any[], discount: any): number {
  const { rules } = discount.config;

  // Abort if basket does not meet the minimum quantity needed to apply the discount
  if (isBefore(parseISO(`${rules.validUntil}T00:00:00Z`), new Date())) {
    return 0;
  }

  const items = courses
    .filter((x) => {
      return discount.events.find((y) => y.event_detail_id === x.detail_id);
    })
    .map((x) => {
      const price = x.standard_price
        ? x.standard_price
        : x.seat_price
          ? x.seat_price
          : 0;
      const discountAmount =
        discount.type === 'percent'
          ? (discount.value / 100) * price
          : discount.value;

      return {
        ...x,
        discountAmount,
      };
    });

  return items.reduce((a, b) => a + b.discountAmount, 0);
}

type Invoice = any;
type Enrollment = any;
